import { Add, AssignmentOutlined, ChevronRight, DeleteOutlined } from "@mui/icons-material";
import { Alert, Box, Fab, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import copy from "fast-copy";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { AddEditTask, TreeItemLoader } from "../../components";
import { useAppDispatch } from "../../redux";
import { ITask, useDeleteTaskMutation, useLazyGetTaskByIdQuery, useLazyGetTasksQuery } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateCongirmationDialogBody } from "../../redux/slices/confirmationDialog";
import { openDialog } from "../../redux/slices/dialog";

const EndIcon: React.FC = () => <ChevronRight color="disabled" />;

export type taskTree = ITask & { index: number[], child_tasks?: taskTree[] }

export interface taskHistoryInterface { label: string, value: string }

export const SubTaskTree: React.FC<{
    parentTaskId: string,
    selectedTask?: string,
    onTaskSelected?: (task: taskTree, parentTask?: taskTree) => void,
    updateHistory?: (history: taskHistoryInterface[], currentPath: string) => void,
}> = React.memo(({
    parentTaskId,
    selectedTask,
    updateHistory,
    onTaskSelected,
}) => {

    const dispatch = useAppDispatch();

    const [fetchTasks, tasksData] = useLazyGetTasksQuery();
    const [fetchTaskById] = useLazyGetTaskByIdQuery();
    const [deleteTask] = useDeleteTaskMutation();

    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [selected, setSelected] = React.useState<string>('');
    const [tasks, setTasks] = React.useState<taskTree[]>([]);
    const [currentItem, setCurrentItem] = React.useState<taskTree | null>(null);
    const [parentItem, setParentItem] = React.useState<taskTree | undefined>();
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | HTMLLIElement | null>(null);
    const [anchorElForFolderActions, setFolderActions] = React.useState<HTMLDivElement | HTMLLIElement | null>(null);
    const [history, setHistory] = React.useState<taskHistoryInterface[]>([]);
    const [treeBuilding, setTreeBuilding] = React.useState<boolean>(false);

    const menuRef = React.useRef<HTMLDivElement | null>(null);

    const buildAndUpdateHistory = (task: taskTree) => {
        let newHistory: taskHistoryInterface[] = [];
        let parentTask: taskTree = tasks[task.index[0]];

        for (let i = 1; i < task.index.length; i++) {
            newHistory.push({ label: `${parentTask.sequence_id}`, value: parentTask._id })
            if (parentTask.child_tasks?.length) {
                parentTask = parentTask.child_tasks[task.index[i]];
            } else {
                break;
            }
        }

        if (updateHistory) {
            updateHistory(newHistory, `${task.sequence_id ?? ''}`);
        };

        setHistory(newHistory);
    };

    const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => setExpanded(nodeIds);

    const handleSelect = (nodeIds: string) => setSelected(nodeIds);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFolderActionsClose = () => {
        setFolderActions(null);
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        event.currentTarget.click();
        event.preventDefault();

        if (selected === currentItem?._id) {
            setAnchorEl(event.currentTarget);
        } else {
            setAnchorEl(null);
        }
    };

    const onTaskClicked = (task: taskTree, parentTask?: taskTree) => {
        setCurrentItem(task);
        setParentItem(parentTask);

        document.getElementById(`task-id-${task?._id}`)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "start" });

        fetchTaskChildrensById({ taskId: task._id, index: task?.index ?? [] });

        onTaskSelected?.(task, parentTask);
        buildAndUpdateHistory(task);
    };

    const giveMeTreeItems = (task: taskTree, parentTask?: taskTree) => {
        return <TreeItem
            itemId={task._id}
            label={
                <Stack onContextMenu={handleClick} direction={"row"} gap={1} alignItems={"center"} sx={{ width: "100%" }}>
                    <AssignmentOutlined fontSize="small" />
                    <Box>
                        <Typography sx={{
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            WebkitLineClamp: 2,
                            lineClamp: 2,
                        }} variant="body2" title={`${task.name} (${task.sequence_id})`}>
                            {`${task.name}`}
                            <Typography variant="caption" color="textSecondary">{` (${task.sequence_id})`}</Typography>
                        </Typography>
                    </Box>
                </Stack>
            }
            id={`task-id-${task._id}`}
            onClick={() => onTaskClicked(task, parentTask)}
        >
            {task?.child_tasks?.map(child => {
                return giveMeTreeItems(child, task)
            })}

            {(tasksData.isLoading || tasksData.isFetching) && (currentItem?._id === task._id) && <Box py={1}>
                <TreeItemLoader />
            </Box>}
        </TreeItem>
    };

    const massageTaskForState = (taskToMassage: ITask[], parentIndices: number[]) => {
        return taskToMassage?.map((_, index) => ({ ..._, child_tasks: [], index: [...parentIndices, index] })) || []
    };

    const updateTasksRecursively = (tasks: taskTree[], newTasks: taskTree[], index: number[], allIndex: number[]): taskTree[] => {
        if (index.length === 1) {
            tasks[index[0]].child_tasks = massageTaskForState(newTasks || [], allIndex);
        } else {
            tasks[index[0]].child_tasks = updateTasksRecursively(tasks[index[0]].child_tasks || [], newTasks, index.slice(1, index.length), allIndex);
        }
        return tasks;
    };

    const addItem = (childrenTasks: taskTree[], index: number[], extTasks?: taskTree[]) => {
        let newTasks: taskTree[] = extTasks ? copy(extTasks) : copy(tasks);

        if (index.length === 1) {
            newTasks[index[0]].child_tasks = massageTaskForState(childrenTasks, index);
        } else {
            newTasks[index[0]].child_tasks = updateTasksRecursively(newTasks[index[0]].child_tasks || [], childrenTasks, index.slice(1, index.length), index);
        }

        setTasks([...newTasks]);

        if (extTasks) {
            setExpanded([newTasks[0]._id]);
            setSelected(newTasks[0]._id);
            setCurrentItem(newTasks[0]);
            setTreeBuilding(false);
            buildAndUpdateHistory(newTasks[0])
        }
    };

    const fetchTaskChildrensById = async ({ taskId, index, newTasks }: { taskId?: string, index: number[], newTasks?: taskTree[] }) => {

        const { data } = await fetchTasks({
            parent_object_id: taskId,
            parent_object_type: 'task',
            sort_by: 'name', sort_order: 'asc',
            page_size: 10000,
        });

        addItem((data?.data || []) as taskTree[], index, newTasks);
    };

    const addEditTask = () => {
        dispatch(openDialog({
            title: `Create new Sub Task`,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditTask
                subTaskParents={history}
                parentTask={currentItem as ITask}
                parentObjectId={`${currentItem?._id ?? ""}`}
                parentObjectType="task"
                isEdit={false}
                refetch={() => {
                    let _id = currentItem ? currentItem._id : undefined;
                    let index = currentItem?.index ?? [];
                    fetchTaskChildrensById({ taskId: _id, index: index })
                    setExpanded((oldExpanded) => [...oldExpanded, `${_id}`]);
                }}
            />
        }))
    };

    const handleDeleteTask = () => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: `Do you want to delete the task: ${currentItem?.name}?`,
            positiveBtn: 'Delete',
            negativeBtn: 'Cancel',
            onOk: () => confirmDeleteTaskDelete(),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const confirmDeleteTaskDelete = async () => {
        dispatch(openBackdrop('Deleting task...'));

        await deleteTask(`${currentItem?._id ?? ""}`).then((res: any) => {

            if (res.error) {
                let errMessage = res.error?.data?.title ?? 'Something went wrong, Unable to delete task.';
                enqueueSnackbar(errMessage, { variant: "error" });
                dispatch(updateCongirmationDialogBody(<Stack gap={2}>
                    <Typography gutterBottom variant="body1">{`Do you want to delete the task: ${currentItem?.name}?`}</Typography>
                    <Alert severity="warning">{errMessage}</Alert>
                </Stack>));
            } else {
                fetchTaskChildrensById({ taskId: parentItem ? parentItem._id : undefined, index: parentItem?.index ?? [] });
                enqueueSnackbar('Deleted the task', { variant: "success" });
                dispatch(closeConfirmationDialog());
            }
        }).catch((err) => {
            enqueueSnackbar(err.data?.title ?? 'Something went wrong, Unable to delete task.', { variant: "error" });
        });

        dispatch(closeBackdrop());
    };

    const fetchParent = async () => {
        setTreeBuilding(true);
        const taskDetail = await fetchTaskById(parentTaskId).unwrap();
        let newTasks: taskTree[] = [{ ...taskDetail, child_tasks: [], index: [0] }];
        fetchTaskChildrensById({ taskId: taskDetail._id, index: [0], newTasks: newTasks });
    };

    const open = Boolean(anchorEl);
    const id = open ? 'treeItem-' + currentItem?._id : undefined;

    const openFolderActions = Boolean(anchorElForFolderActions);
    const folderActionsId = openFolderActions ? 'folder-root-actions-' + currentItem?._id : undefined;

    React.useEffect(() => {
        fetchParent();
        // eslint-disable-next-line
    }, [parentTaskId]);

    React.useEffect(() => {
        if (selectedTask && (selectedTask !== parentTaskId)) {
            const element: any = document.getElementById(`task-id-${selectedTask}`);

            if (element) {
                element?.children[0].click(); // Trigger click event
            }
        }
        // eslint-disable-next-line
    }, [selectedTask]);

    return <Box sx={{ height: "100%", overflowX: "hidden" }}>
        {/* Folder Tree */}
        <SimpleTreeView
            slots={{ endIcon: EndIcon }}
            expandedItems={expanded}
            selectedItems={selected}
            onExpandedItemsChange={handleToggle}
            onSelectedItemsChange={(e, itemIds) => itemIds ? handleSelect(itemIds) : false}
        >
            {tasks.map((task) => giveMeTreeItems(task))}
        </SimpleTreeView>

        {treeBuilding && <Box py={1}>
            <TreeItemLoader />
        </Box>}

        <Box sx={{ position: "absolute", bottom: "8px", right: "8px" }}>
            <Stack onClick={parseInt(`${currentItem?.index?.length ?? 0}`) >= 10 ? () => { } : handleClick} ref={menuRef}>
                <Fab
                    size="small"
                    color="primary"
                    variant={"circular"}
                    disabled={parseInt(`${currentItem?.index?.length ?? 0}`) >= 10}
                >
                    <Add />
                </Fab>
            </Stack>

            <Menu
                id={folderActionsId}
                open={openFolderActions}
                anchorEl={anchorElForFolderActions}
                onClose={handleFolderActionsClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {[
                    { showIcon: true, disabled: false, hide: false, icon: <Add htmlColor="primary" />, name: "New Task", onClick: () => addEditTask() },
                    { showIcon: true, disabled: false, hide: parentTaskId === currentItem?._id, icon: <DeleteOutlined color="error" />, name: "Delete", onClick: handleDeleteTask },
                ]?.filter(option => !option.hide).map((option, index) => (<MenuItem key={index} disabled={option.disabled}
                    sx={{ borderTop: (theme) => [1, 2].includes(index) ? `1px solid ${theme.palette.divider}` : `` }}
                    onClick={() => {
                        option.onClick();
                        handleFolderActionsClose();
                    }}>
                    {option.showIcon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText>{option.name}</ListItemText>
                </MenuItem>))}
            </Menu>
        </Box>

        <Menu
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
        >
            {[
                { showIcon: true, disabled: false, hide: parseInt(`${currentItem?.index?.length ?? 0}`) >= 10, icon: <Add color="primary" />, name: "New Task", onClick: () => addEditTask() },
                { showIcon: true, disabled: true, hide: !(parseInt(`${currentItem?.index?.length ?? 0}`) >= 10), icon: <></>, name: "Maximum depth (9) acheived", onClick: () => false },
            ]?.filter(opt => opt.hide === false).map((option, index) => (<MenuItem key={index} disabled={option.disabled}
                sx={{ borderTop: (theme) => [1, 2, 3].includes(index) ? `1px solid ${theme.palette.divider}` : `` }}
                onClick={() => {
                    option.onClick();
                    setAnchorEl(null);
                }}>
                {option.showIcon && <ListItemIcon>{option.icon}</ListItemIcon>}
                <ListItemText>{option.name}</ListItemText>
            </MenuItem>))}
        </Menu>
    </Box>
})